import { intl } from '@fullstory/intl';
import { DimensionSettings, SortOrder, SortType } from '@fullstory/proto/fs/api/metrics';
import { Event, TimeInterval, TimeRange } from '@fullstory/proto/fs/api/segment';
import {
  Color,
  ConversionFunnelSettings,
  DateRangeId,
  DateRangeSetting,
  DimensionalitySettings,
  SingleNumberSettings,
  TrendFrequency,
  TrendSettings,
} from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import {
  DEFAULT_DIMENSION_NO_CONFIG,
  DEFAULT_DIMENSION_SETTINGS_BROWSER,
  DEFAULT_DIMENSION_SETTINGS,
  DEFAULT_DIMENSION_STATE,
  DEFAULT_DIMENSION,
  DEFAULT_DISPLAY,
  type DimensionSettingsNoDimensions,
} from '@fullstory/metrics-model';
import {
  DEFAULT_SEGMENT,
  DEFAULT_SEGMENT_OPTION,
  DEFAULT_SEGMENTS,
  EVERYONE_SEGMENT_ID,
} from '@fullstory/segments-model';
import { int64 } from '@fullstory/proto/google/protobuf';
import { dateRangeSettingToTimeRange } from '@fullstory/redux';
import { getDateRangeIdFromTimeRange } from '@fullstory/search-model';
import { Theme } from '../types/types';
import { getDefaultFrequencyForTimeRange, getDefaultTimeRange } from './timeRange';

export const WELCOME_DASHBOARD_ID = '-1';

export const FROM_DASHBOARD_PARAM = 'fromDashboard';
export const FROM_DASHBOARD_CARD_PARAM = 'fromCard';

export const getAnyActivityEvent: () => Event = () => ({
  primary: null,
  deps: [],
});

export const UNKNOWN_LABEL = intl.formatMessage({ defaultMessage: 'Unknown', id: '5jeq8P' });

export const NOT_DEFINED_LABEL = intl.formatMessage({
  defaultMessage: 'Not defined',
  id: 'O7foWm',
});

export const OTHER_LABEL = intl.formatMessage({ defaultMessage: 'Other', id: '/VnDMl' });

export { EVERYONE_SEGMENT_ID };

export const DEFAULT_SESSION_PLAYLIST_SORT_ORDER = SortOrder.SORT_ORDER_DESC;

export { DEFAULT_SEGMENT, DEFAULT_SEGMENTS, DEFAULT_SEGMENT_OPTION };

export const DEFAULT_FUNNEL_DIMENSION_SETTINGS: DimensionSettings = {
  dimension: null,
  sortOrder: SortOrder.SORT_ORDER_DESC,
  sortByDefault: false,
  sortType: SortType.SORT_TYPE_INVALID,
  limit: 10,
  dimensions: [],
  computeTopLevel: false,
  computeNumGroups: false,
  allowAllPartialResults: false,
  noFallback: false,
};

export const DEFAULT_PAGEFLOW_DIMENSION_SETTINGS: DimensionSettings = {
  dimension: null,
  sortOrder: SortOrder.SORT_ORDER_DESC,
  sortByDefault: false,
  sortType: SortType.SORT_TYPE_INVALID,
  limit: 0,
  dimensions: [],
  computeTopLevel: false,
  computeNumGroups: false,
  allowAllPartialResults: false,
  noFallback: false,
};

export const DEFAULT_RETENTION_DIMENSION_SETTINGS: DimensionSettings = {
  dimension: null,
  sortOrder: SortOrder.SORT_ORDER_DESC,
  sortByDefault: false,
  sortType: SortType.SORT_TYPE_INVALID,
  limit: 0,
  dimensions: [],
  computeTopLevel: false,
  computeNumGroups: false,
  allowAllPartialResults: false,
  noFallback: false,
};

export {
  type DimensionSettingsNoDimensions,
  DEFAULT_DIMENSION_NO_CONFIG,
  DEFAULT_DIMENSION_SETTINGS_BROWSER,
  DEFAULT_DIMENSION_SETTINGS,
  DEFAULT_DIMENSION_STATE,
  DEFAULT_DIMENSION,
};

export const defaultFilterTheme: Theme = {
  filterTextColor: 'neutrals-n10',
  objectTypeFilter: {
    default: {
      background: 'neutrals-n5',
      text: 'neutrals-n9',
    },
    selected: {
      background: 'neutrals-n11',
      text: 'neutrals-n0',
    },
  },
};

export { DEFAULT_DISPLAY };

export const DEFAULT_DATE_RANGE: DateRangeSetting = {
  dateRangeId: DateRangeId.DATE_RANGE_ID_PAST_WEEK,
  startTime: null,
  endTime: null,
};

export const DEFAULT_SINGLE_NUMBER_VISUALIZATION_SETTINGS = (
  orgUseWeeklyEveryone: boolean,
): SingleNumberSettings => ({
  segment: DEFAULT_SEGMENT,
  segmentId: [],
  conversionId: [],
  computePast: false,
  color: Color.COLOR_INVALID,
  displayAs: DEFAULT_DISPLAY,
  timeRange: getDefaultTimeRange(orgUseWeeklyEveryone),
  timeRangeComparison: null,
});

export const DEFAULT_TREND_VISUALIZATION_SETTINGS = (
  orgUseWeeklyEveryone: boolean,
  timeRange?: TimeRange | null,
): TrendSettings => {
  const trendTimeRange = timeRange ?? getDefaultTimeRange(orgUseWeeklyEveryone);
  const formattedTimeRange = {
    Start: trendTimeRange.startTime,
    End: trendTimeRange.endTime,
  };
  const dateRangeId = getDateRangeIdFromTimeRange(formattedTimeRange);
  const frequency =
    getDefaultFrequencyForTimeRange(dateRangeId, formattedTimeRange) ??
    TrendFrequency.TREND_FREQUENCY_DAILY;
  return {
    segment: [DEFAULT_SEGMENT],
    segmentId: [],
    conversionId: [],
    computePast: false,
    displayAs: DEFAULT_DISPLAY,
    color: Color.COLOR_INVALID,
    timeRange: trendTimeRange,
    timeRangeComparison: null,
    dimensionSettings: null,
    frequency,
  };
};

export const DEFAULT_DIMENSIONALITY_VISUALIZATION_SETTINGS = (
  orgUseWeeklyEveryone: boolean,
): DimensionalitySettings => ({
  segment: DEFAULT_SEGMENT,
  segmentId: [],
  computePast: false,
  displayAs: DEFAULT_DISPLAY,
  color: Color.COLOR_INVALID,
  timeRange: getDefaultTimeRange(orgUseWeeklyEveryone),
  timeRangeComparison: null,
  dimensionSettings: DEFAULT_DIMENSION_SETTINGS_BROWSER,
});

export const DEFAULT_FUNNEL_VISUALIZATION_SETTINGS = (): ConversionFunnelSettings => ({
  segment: [DEFAULT_SEGMENT],
  segmentId: '',
  conversionId: int64.zero,
  dateRange: DEFAULT_DATE_RANGE,
  color: Color.COLOR_INVALID,
  dimensionSettings: null,
  dimensionApplication: null,
  timeRange: dateRangeSettingToTimeRange(DEFAULT_DATE_RANGE),
  timeRangeComparison: null,
  timeInterval: TimeInterval.TIME_INTERVAL_INVALID,
  trendSteps: null,
});
